import { defineRule } from "vee-validate";
import { required, email, min, max } from "@vee-validate/rules";
import { DateValidationService } from "@/services/validation/DateValidationService";
import i18n from "@/i18n";
declare global {
  interface String {
    pathStartsWith(innerPath: string): boolean;
  }
}

/**
 * Services in charge of handling validation rules (by customizing vee-validate rules)
 */
export class ValidationService {
  static init(): void {
    // Register date specific rules
    DateValidationService.init();

    // Register all used rules here
    // Complete list can be found here https://www.npmjs.com/package/@vee-validate/rules
    defineRule("min", (value: string, expectedMin: [string | number], ctx: { field: string }): boolean | string => {
      if (min(value, expectedMin)) {
        return true;
      }
      else {
        const translatedField: string = i18n.global.t(ctx.field);
        return i18n.global.t("validation.invalid-min", {
          field: translatedField,
          length: expectedMin[0],
        });
      }
    });

    defineRule("max", (value: string, expectedMax: [string | number], ctx: { field: string }): boolean | string => {
      if (max(value, expectedMax)) {
        return true;
      }
      else {
        const translatedField: string = i18n.global.t(ctx.field);
        return i18n.global.t("validation.invalid-max", {
          field: translatedField,
          length: expectedMax[0],
        });
      }
    });

    defineRule("length", (value: string, params: string[], ctx: { field: string }): boolean | string => {
      const expectedLength: number = Number(params[0]);
      if (String(value).length === expectedLength) {
        return true;
      }
      else {
        const translatedField: string = i18n.global.t(ctx.field);
        return i18n.global.t("validation.invalid-length", {
          field: translatedField,
          length: expectedLength,
        });
      }
    });

    defineRule("email", (value: string, _param: { length: number }, ctx: { field: string }): boolean | string => {
      if (email(value)) {
        return true;
      }
      else {
        const translatedField: string = i18n.global.t(ctx.field);
        return i18n.global.t("validation.invalid-email", { field: translatedField, email: value });
      }
    });

    defineRule("required", (value: string) => {
      if (required(value)) {
        return true;
      }
      else {
        return i18n.global.t("validation.invalid-required");
      }
    });

    defineRule("confirmed", (confirmationValue: string, [target]: string, ctx: { field: string; form: unknown }): boolean | string => {
      // Get the target value (e.g. if target is 'register.password', get ctx.form[register][password])
      let formTarget = ctx.form;
      const splittedTarget: string[] = target.split(".");
      for (let i = 0; i < splittedTarget.length && formTarget; i++) {
        // @ts-expect-error as form is not correctly typed in vee-validate, no way to further type this
        formTarget = formTarget[splittedTarget[i]];
      }
      if (confirmationValue === formTarget) {
        return true;
      }
      else {
        const translatedConfirmationField: string = i18n.global.t(ctx.field);
        const translatedOriginalField: string = i18n.global.t(target);
        return i18n.global.t("validation.invalid-confirmed", {
          confirmedField: translatedConfirmationField,
          field: translatedOriginalField,
        });
      }
    });

    defineRule("validApplicationName", (value: string): boolean | string => {
      const isValidApplicationName: boolean = /^[a-z]*$/.test(value);
      if (isValidApplicationName) {
        return true;
      }
      else {
        return i18n.global.t("validation.invalid-application-name");
      }
    });

    defineRule("validApplicationNameLength", (value: string): boolean | string => {
      const len: number = String(value || "").length;
      if (len >= 3 && len <= 20) {
        return true;
      }
      else {
        return i18n.global.t("validation.invalid-application-name-length");
      }
    });

    defineRule("selected", (value: string): boolean | string => {
      if (Object.keys(value).length > 0) {
        return true;
      }
      else {
        return i18n.global.t("rules.required");
      }
    });
    defineRule("regexp", (value: string | string[], params: string[]) => {
      try {
        // Need to escape "|" characters (interpreted as validation rules separator)
        const pattern = params[0].replaceAll("ESCAPED_OR", "|");
        const regex: RegExp = new RegExp(pattern);
        let matchesRegExp: boolean = false;
        if (Array.isArray(value)) {
          matchesRegExp = value.every(regex.test);
        }
        else {
          matchesRegExp = regex.test(value);
        }
        if (matchesRegExp) {
          return true;
        }
        else {
          return i18n.global.t("rules.regexp", {
            pattern: pattern,
          });
        }
      }
      catch (e) {
        return "Erreur lors du test de l'expression " + params[0] + " " + e;
      }
    });

    defineRule("integer", (value: string | string[]): boolean | string => {
      const integerRegExp: RegExp = new RegExp("^[-+]?\\d+$", "g");
      let matchesIntegerRegExp: boolean = false;
      if (Array.isArray(value)) {
        matchesIntegerRegExp = value.every(integerRegExp.test);
      }
      else {
        matchesIntegerRegExp = integerRegExp.test(value);
      }
      if (matchesIntegerRegExp) {
        return true;
      }
      else {
        return i18n.global.t("rules.integer");
      }
    });

    defineRule("float", (value: string | string[]): boolean | string => {
      const floatRegExp: RegExp = new RegExp("^[+-]?([0-9]*[.,])?[0-9]+$", "g");
      let matchesFloatRegExp: boolean = false;
      if (Array.isArray(value)) {
        matchesFloatRegExp = value.every(floatRegExp.test);
      }
      else {
        matchesFloatRegExp = floatRegExp.test(value);
      }
      if (matchesFloatRegExp) {
        return true;
      }
      else {
        return i18n.global.t("rules.float");
      }
    });

    String.prototype.pathStartsWith = function (innerPath: string): boolean {
      innerPath = innerPath || "";
      return this === innerPath || this.startsWith(innerPath + ".");
    };
  }
}
